import {
  Box,
  CircularProgress,
  Collapse,
  collapseClasses,
  Container,
  Typography,
} from '@mui/material';
import React from 'react';
import TitleIcon from '../../../../../../theme/ui/Atoms/TilteIcon/TitleIcon';
import { ViewLocationIcon } from '../../../../../../assets';
import useTypeEntity from '../useTypeEntity';
import { useTranslation } from 'react-i18next';
import { SubHeaderLocations } from './SubHeaderLocations';
import { SearchBar } from '../../../../../../theme/ui/Atoms/SearchBar/SearchBar';
import { NormalizedGroup } from '../../../../../../redux/structure/GroupsAdapter';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { WorkspaceAPIResponse } from '../../../../../../redux/workspace/WorkspaceAPI.model';

interface LocationsLayoutProps {
  children: React.ReactNode;
  innerContainerMaxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  role: string;
  showRightPanel?: boolean;
  rightPanel?: React.ReactNode;
}
function LocationsLayout(props: LocationsLayoutProps) {
  const {
    children,
    innerContainerMaxWidth = 'lg',
    role,
    showRightPanel = false,
    rightPanel,
  } = props;
  const { entity, type, isLoading, groupId, pathGroups, marketId } =
    useTypeEntity();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const onSearch = (item: NormalizedGroup | WorkspaceAPIResponse) => {
    if ((item as NormalizedGroup).uuid) {
      const groupId = (item as NormalizedGroup).uuid;
      if (marketId) {
        return navigate(`/markets/${marketId}/view-locations/${groupId}`);
      }
      return navigate(`/manager/view-locations/${groupId}`);
    }
    const itemMarketId = (item as WorkspaceAPIResponse).id;
    if (itemMarketId) {
      return navigate(`/markets/${itemMarketId}/view-locations`);
    }
  };
  const title = moment().isBefore(moment().startOf('day').add(12, 'hours'))
    ? t('general.good_morning')
    : t('general.good_evening');
  return (
    <Box>
      {(groupId || marketId) && (
        <SubHeaderLocations
          marketId={marketId}
          entity={entity}
          type={type}
          loading={isLoading}
          pathGroups={pathGroups}
        />
      )}
      <Container maxWidth="xl">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            my: 4,
          }}
        >
          <Box flex={'2 1 auto'}>
            <Box>
              <Typography
                variant="h2"
                fontWeight={'bold'}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
                mt={4}
                mb={0}
                color="primary"
              >
                {title}
              </Typography>
            </Box>
          </Box>
          <Box flex={'1 1 auto'}>
            <SearchBar selectItem={onSearch}>
              <SearchBar.MarketsSearch />
              <SearchBar.BUSearch />
              <SearchBar.RegionSearch />
              <SearchBar.StoreSearch />
            </SearchBar>
          </Box>
        </Box>
        <TitleIcon
          icon={
            isLoading ? (
              <CircularProgress size={'2em'} />
            ) : (
              <ViewLocationIcon height={'2em'} />
            )
          }
        >
          {t('view_locations.view_all')} {t(`view_locations.${role}`)}
        </TitleIcon>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            mt: 4,
            overflow: 'hidden',
          }}
        >
          <Box
            className="transition-p"
            sx={[
              {
                transition: 'flex-basis 0.3s ease-in-out 0.225s',
                overflow: 'auto',
              },
              showRightPanel
                ? {
                    flex: '0 0 60%',
                  }
                : {
                    flex: '1 1 100%',
                  },
            ]}
          >
            <Container
              maxWidth={innerContainerMaxWidth}
              sx={{
                overflow: 'auto',
              }}
            >
              {children}
            </Container>
          </Box>

          <Collapse
            orientation="horizontal"
            in={showRightPanel}
            mountOnEnter
            unmountOnExit
            sx={{
              flex: '0 0 40%',
              overflow: 'auto',
              [`& .${collapseClasses.wrapperInner}`]: {
                width: '100%',
              },
            }}
          >
            {rightPanel}
          </Collapse>
        </Box>
      </Container>
    </Box>
  );
}

export default LocationsLayout;
